// src/pages/PhotoshootAnyshootImages.jsx
import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  Plus,
  Download,
  Trash2,
  X,
  ExternalLink,
  AlertCircle,
} from "lucide-react";
import GeneratePhotoshootAnyshootModal from "../components/GeneratePhotoshootAnyshootModal";
import { useAuth } from "@clerk/clerk-react";
import { useProductStore } from "../store/useProductStore";
import Loader from "../components/Loader"; // ✅

/* ------------------------------ Simple toast ------------------------------ */
function Toast({ message, onClose }) {
  useEffect(() => {
    if (!message) return;
    const timeout = setTimeout(onClose, 3500);
    return () => clearTimeout(timeout);
  }, [message, onClose]);

  if (!message) return null;

  return (
    <div className="fixed top-6 right-8 z-[9999] bg-pink-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-fadeIn border border-pink-400">
      <AlertCircle size={20} />
      <span className="text-sm">{message}</span>
      <button
        className="ml-2 text-white hover:text-black transition"
        onClick={onClose}
      >
        <X size={16} />
      </button>
    </div>
  );
}

const FEATURE = "photoshoot-anyshoot";
const PAGE_SIZE = 12;

export default function PhotoshootAnyshootImages() {
  const { getToken } = useAuth();
  const { selectedProduct } = useProductStore();
  const { projectId: paramProjectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [assets, setAssets] = useState([]);
  const [projectAssets, setProjectAssets] = useState([]); // inputs = analyzed images/videos
  const [modalOpen, setModalOpen] = useState(false);
  const [preview, setPreview] = useState(null); // { url, id }
  const [error, setError] = useState(null);
  const [toast, setToast] = useState("");
  const [queueNotice, setQueueNotice] = useState("");

  // page-level project loading (first load only)
  const [initialLoading, setInitialLoading] = useState(true);

  // per-user image quota
  const [quota, setQuota] = useState({
    loading: true,
    unlimited: false,
    remaining: 0,
    used: 0,
    baseCap: 0,
    imagesExtra: 0,
    period_start: null,
    period_end: null,

    // gating (additive)
    payment_blocked: false,
    sub_status: null,
    is_blocked: false,
    shift_months: 0,
  });

  const previewBackdrop = useRef(null);
  const loaderRef = useRef(null);
  const pollTimerRef = useRef(null);

  // ✅ Infinite scroll (ObjectRemover pattern)
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);

  const projectId = selectedProduct?.id || paramProjectId;

  // Touch the variable so eslint doesn't flag it (no runtime effect)
  // eslint-disable-next-line no-unused-expressions
  void project;

  const isSoftDeleted = useCallback((a) => {
    const v = a?.metadata?.deleted;
    return v === true || v === "true";
  }, []);

  const isPendingStatus = useCallback((statusRaw) => {
    const s = String(statusRaw || "").toLowerCase();
    return s === "pending" || s === "queued" || s === "processing";
  }, []);

  /* -------------------- Derive feature assets, sorted new -> old -------------------- */
  const featureAssets = useMemo(
    () =>
      (assets || [])
        .filter((a) => (a?.metadata?.feature || "").toLowerCase() === FEATURE)
        // ✅ Hide soft-deleted rows so they don't reappear after polling/refresh
        .filter((a) => !isSoftDeleted(a))
        .sort(
          (l, r) =>
            new Date(r.updated_at || r.created_at) -
            new Date(l.updated_at || l.created_at)
        ),
    [assets, isSoftDeleted]
  );

  // ✅ Slice for infinite scroll (client-side pagination) — ObjectRemover pattern
  const pagedAssets = useMemo(
    () => featureAssets.slice(0, page * PAGE_SIZE),
    [featureAssets, page]
  );

  // Global “has any pending” flag (like ProductAvatarImages)
  // ✅ Only pending|queued|processing are pending (failed does NOT poll forever)
  const hasPending = useMemo(
    () => featureAssets.some((a) => isPendingStatus(a?.metadata?.status)),
    [featureAssets, isPendingStatus]
  );

  /* ------------------------- Quota fetcher (images) ------------------------ */
  const fetchQuota = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/product-anyshoot/quota`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuota({ loading: false, ...res.data });
    } catch {
      setQuota((q) => ({ ...q, loading: false }));
    }
  }, [getToken]);

  /* -------------------------- Project fetcher (FE) ------------------------- */
  // NOTE: 'silent' prevents toggling the Loader during polling refreshes.
  const fetchProject = useCallback(
    async ({ silent = false } = {}) => {
      // ✅ If no analyzed project yet, don’t error — just show CTA and empty grid.
      if (!projectId) {
        setProject(null);
        setProjectAssets([]);
        setAssets((prev) => prev.filter((a) => a?.metadata?.local)); // keep optimistic only
        setError(null);
        if (!silent) setInitialLoading(false);
        return;
      }

      if (!silent) setInitialLoading(true);
      try {
        const token = await getToken();
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setProject(data);
        setAssets(Array.isArray(data.assets) ? data.assets : []);
        const inputs = [
          ...(Array.isArray(data.images) ? data.images : []),
          ...(Array.isArray(data.videos) ? data.videos : []),
        ];
        setProjectAssets(inputs);
        setError(null);
      } catch {
        setError("Failed to fetch project data.");
      } finally {
        if (!silent) setInitialLoading(false);
      }
    },
    [getToken, projectId]
  );

  /* --------------------------- Initial load + quotas ----------------------- */
  useEffect(() => {
    setInitialLoading(true);
    setPage(1);
    pageRef.current = 1;
    setHasMore(true);
    void fetchProject({ silent: false });
    void fetchQuota();
  }, [fetchProject, fetchQuota, projectId]);

  // keep route in sync with header product dropdown
  useEffect(() => {
    if (selectedProduct?.id && selectedProduct.id !== paramProjectId) {
      navigate(`/generate/photoshoot-anyshoot/${selectedProduct.id}`);
    }
  }, [selectedProduct, paramProjectId, navigate]);

  /* --------------------------- Polling while pending ----------------------- */
  useEffect(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }

    if (hasPending && projectId) {
      // lightweight poll — silent refresh to avoid flicker
      pollTimerRef.current = setInterval(() => {
        fetchProject({ silent: true });
      }, 3000);
    } else {
      // when a polling cycle ends, refresh quota to capture final usage
      void fetchQuota();
    }

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [hasPending, fetchProject, fetchQuota, projectId]);

  /* --------------------------- Infinite scroll (ObjectRemover pattern) --------------------------- */
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    setHasMore(page * PAGE_SIZE < featureAssets.length);
  }, [page, featureAssets.length]);

  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPage((p) => p + 1);
    setLoadingMore(false);
  }, [loadingMore]);

  useEffect(() => {
    if (!hasMore || initialLoading || loadingMore) return;
    const el = loaderRef.current;
    if (!el) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "100px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, initialLoading, loadingMore, loadMore]);

  /* --------------------------- Generate handler ---------------------------- */
  // Generate handler — expects the modal to pass the same body as before
  const handleGenerate = async (params) => {
    if (!projectId) {
      setToast(
        "Please analyze a product first so we can save your generated assets."
      );
      navigate("/analyze");
      return;
    }

    if (quota?.is_blocked) {
      setToast("Your account is blocked. Please contact support.");
      return;
    }

    if (quota?.payment_blocked) {
      navigate("/pricing");
      return;
    }

    const count = Math.max(1, Number(params?.generatingCount) || 1);
    const canGenNow = quota.unlimited || (quota.remaining || 0) >= count;

    if (!canGenNow) {
      navigate("/pricing");
      return;
    }

    try {
      setError(null);
      setToast("");
      setQueueNotice("");

      const token = await getToken();

      const tempIds = Array.from(
        { length: count },
        (_, i) => `temp-${Date.now()}-${i}`
      );

      setAssets((prev) => [
        ...tempIds.map((id) => ({
          id,
          url: "/spinner.gif#pending",
          name: `anyshoot-${id}`,
          type: "photo",
          metadata: { feature: FEATURE, status: "pending", local: true },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })),
        ...prev,
      ]);

      if (!quota.unlimited) {
        setQuota((q) => ({
          ...q,
          remaining: Math.max(0, (q.remaining || 0) - count),
          used: (q.used || 0) + count,
        }));
      }

      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/product-anyshoot/generate`,
        params,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const pendingList = Array.isArray(data?.pendingAssets)
        ? data.pendingAssets
        : [];

      if (!pendingList.length) {
        setAssets((prev) => prev.filter((a) => !a.metadata?.local));
        setError("Failed to queue job.");
        setToast("Failed to queue job.");
        void fetchQuota();
        return;
      }

      setAssets((prev) => [
        ...pendingList,
        ...prev.filter((a) => !a.metadata?.local),
      ]);

      if (data?.queueStatus === "queued") {
        setQueueNotice(
          "Your request has been queued successfully. It will start shortly, and you’ll receive a notification when it’s ready."

        );
      }
    } catch (e) {
      setAssets((prev) => prev.filter((a) => !a.metadata?.local));
      setError("Failed to start generation.");
      setToast("Failed to start generation.");
      setQueueNotice("");
      void fetchQuota();
    }
  };

  /* ------------------------- Cloudinary download helper -------------------- */
  // Force-download via Cloudinary (no new tab). Adds fl_attachment to /upload/ URLs.
  const toCloudinaryDownloadUrl = useCallback((url) => {
    if (!url) return url;
    try {
      const u = new URL(url);
      if (u.hostname.includes("cloudinary") && u.pathname.includes("/upload/")) {
        u.pathname = u.pathname.replace("/upload/", "/upload/fl_attachment/");
        return u.toString();
      }
    } catch {
      // ignore parse errors
    }
    return url;
  }, []);

  const handleDownload = (url) => {
    const dl = toCloudinaryDownloadUrl(url);
    const a = document.createElement("a");
    a.href = dl;
    a.download = ""; // let Cloudinary set a good filename via fl_attachment
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDelete = async (assetId) => {
    if (!assetId) return;
    if (!window.confirm("Delete this image?")) return;
    try {
      const token = await getToken();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/product-anyshoot/${assetId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setAssets((prev) => prev.filter((a) => a.id !== assetId));
      if (preview?.id === assetId) setPreview(null);
      // refresh quota on delete, same as other generators
      void fetchQuota();
    } catch {
      alert("Delete failed");
    }
  };

  /* ------------------------------ Preview modal ---------------------------- */
  const handlePreview = (asset) => setPreview({ url: asset.url, id: asset.id });

  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => e.key === "Escape" && setPreview(null);
    const onClick = (e) => {
      if (previewBackdrop.current && e.target === previewBackdrop.current)
        setPreview(null);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [preview]);

  /* ------------------------------ Quota UI ------------------------------ */
  const canGenerate =
    !quota?.is_blocked &&
    !quota?.payment_blocked &&
    (quota.unlimited || (quota.remaining || 0) > 0);

  const totalImages = quota.unlimited
    ? Infinity
    : (quota.baseCap || 0) + (quota.imagesExtra || 0);

  const remaining = quota.unlimited ? Infinity : quota.remaining || 0;
  const usedImages = quota.unlimited ? 0 : quota.used || 0;

  const showBar =
    !quota.loading &&
    !quota.unlimited &&
    Number.isFinite(totalImages) &&
    totalImages > 0;

  const pct = showBar
    ? Math.min(100, Math.max(0, Math.round((usedImages / totalImages) * 100)))
    : 0;

  /* ------------------------- Initial Loader gate --------------------------- */
  // Show Loader only on the very first load (project or quota)
  const isInitialLoading =
    (initialLoading || quota.loading) &&
    !error &&
    featureAssets.length === 0 &&
    !!projectId; // ✅ if no projectId, don’t block the page with a loader

  /* -------------------------------- Render -------------------------------- */
  return (
    <div className="w-full px-2 sm:px-4 md:px-8 py-5 sm:py-8">
      <Toast message={toast} onClose={() => setToast("")} />

      <div className="max-w-[1600px] mx-auto bg-[#15152B] border border-[#2A2A40] rounded-3xl px-2 sm:px-4 md:px-8 py-6 sm:py-10 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            Product Image Photoshoots
          </h1>

          {/* Quota pill (same pattern style-wise as ProductAvatarImages) */}
          {!quota.loading && (
            <div
              className={`flex items-center gap-3 rounded-2xl px-4 py-2 border ${
                quota.unlimited
                  ? "border-pink-600/60 bg-pink-600/10"
                  : "border-[#2A2A40] bg-[#1C1C34]"
              }`}
              title={
                quota.unlimited
                  ? "Unlimited images this cycle"
                  : "Images usage this cycle"
              }
            >
              {quota.unlimited ? (
                <span className="text-pink-400 font-semibold text-sm sm:text-base">
                  Unlimited images
                </span>
              ) : (
                <span className="text-white/80 text-sm sm:text-base">
                  <span className="font-semibold text-white">{remaining}</span>{" "}
                  left of{" "}
                  <span className="font-semibold text-white">
                    {totalImages === Infinity ? "∞" : totalImages}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Usage bar */}
        {showBar && (
          <div className="mb-3 mt-2">
            <div className="h-2 w-full bg-[#21213a] rounded-full overflow-hidden border border-[#2A2A40]">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  background:
                    "linear-gradient(90deg, rgba(252,39,131,0.9) 0%, rgba(252,39,131,0.7) 100%)",
                }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-white/60">
              <span>Used: {usedImages}</span>
              <span>Total: {totalImages === Infinity ? "∞" : totalImages}</span>
            </div>
          </div>
        )}

<p className="text-white/70 mb-4 sm:mb-6 text-sm sm:text-base">
          Instantly generate professional photoshoots for your product using
          hundreds of AI scenes and templates.
        </p>

        {queueNotice && (
          <div className="mb-4 rounded-2xl border border-yellow-500/40 bg-yellow-500/10 px-4 py-3 text-sm text-yellow-200">
            {queueNotice}
          </div>
        )}

        {isInitialLoading ? (
          // Centered Loader on first load
          <div className="flex items-center justify-center py-10 sm:py-16">
            <Loader />
          </div>
        ) : (
          <>
            <div
              className="
              grid gap-4 sm:gap-6
              grid-cols-1
              xs:grid-cols-2
              md:grid-cols-3
              lg:grid-cols-4
              [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]
            "
            >
              {/* ✅ Create CTA card: ALWAYS shown.
                  - No analyzed product -> redirect to /analyze
                  - Out of quota/payment blocked -> redirect to /pricing
                  - Otherwise -> open modal */}
              <div
                onClick={() => {
                  if (!projectId) {
                    navigate("/analyze");
                    return;
                  }
                  if (!canGenerate) {
                    navigate("/pricing");
                    return;
                  }
                  setModalOpen(true);
                }}
                className={`flex flex-col items-center justify-center border-2 border-dashed border-[#3A3A50] text-white rounded-lg min-h-[160px] h-[210px] sm:h-[300px] ${
                  !projectId || !canGenerate
                    ? "opacity-60 cursor-not-allowed"
                    : "cursor-pointer hover:border-pink-500 hover:text-pink-500"
                } transition text-center p-4 sm:p-6`}
                title={
                  !projectId
                    ? "Analyze a product first to start generating"
                    : !canGenerate
                    ? "Out of images. Click to manage plan."
                    : "Pick a product image and template to generate a photoshoot"
                }
              >
                <Plus className="w-7 h-7 sm:w-8 sm:h-8 mb-2" />
                <p className="font-semibold text-base">
                  Create Product Photoshoot
                </p>
                <p className="text-xs sm:text-sm text-gray-400 mt-1">
                  {!projectId
                    ? "Analyze your first product to get started"
                    : quota.unlimited
                    ? "Unlimited images"
                    : `You have ${remaining} left of ${
                        totalImages === Infinity ? "∞" : totalImages
                      } this cycle`}
                </p>
                {!!projectId && !canGenerate && (
                  <p className="text-xs text-red-300 mt-2">
                    You’ve reached your image limit. Upgrade or add images.
                  </p>
                )}
              </div>

              {/* Asset grid (Cloudinary + Assets) */}
              {pagedAssets.map((a) => {
                const status = String(a?.metadata?.status || "").toLowerCase();
                const isPending = isPendingStatus(status);
                const failed = status === "failed" || status === "error";
                const url = isPending ? "/spinner.gif" : a.url;

                return (
                  <div
                    key={a.id}
                    className="relative group bg-gradient-to-br from-[#1C1C2E] to-[#252538] border border-[#2A2A40] text-white rounded-2xl p-2 sm:p-3 transition transform hover:scale-[1.03] hover:shadow-xl hover:ring-1 hover:ring-pink-500/30 min-h-[160px] h-[210px] sm:h-[300px] flex items-center justify-center overflow-hidden"
                  >
                    {/* ✅ Align delete button like Upscaler:
                        - show small top-right delete ONLY for pending/failed cards
                        - for finished cards, delete exists inside hover overlay */}
                    {(isPending || failed) && (
                      <div className="absolute top-2 right-2 z-20">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(a.id);
                          }}
                          className="bg-black/80 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    )}

                    {failed ? (
                      <div className="flex flex-col items-center justify-center w-full h-full">
                        <div className="flex items-center gap-2 text-pink-400">
                          <AlertCircle size={28} className="mb-1" />
                          <span className="font-semibold text-base">
                            Generation Failed
                          </span>
                        </div>
                        <div className="text-xs text-pink-300 mt-2 text-center max-w-[180px] mx-auto">
                          {a.metadata?.reason || "Could not generate image."}
                        </div>
                      </div>
                    ) : isPending ? (
                      <img
                        src="/spinner.gif"
                        alt="Generating..."
                        className="object-contain w-full mx-auto rounded-lg opacity-70 animate-pulse"
                        draggable={false}
                        style={{
                          background: "#111124",
                          height: "100%",
                          maxHeight: "calc(100% - 32px)",
                        }}
                      />
                    ) : (
                      <>
                        <img
                          src={url}
                          className="object-contain max-h-[88%] w-full mx-auto rounded-lg transition-transform duration-300 group-hover:opacity-70 cursor-pointer"
                          onClick={() => handlePreview(a)}
                          alt="Photoshoot"
                          draggable={false}
                          style={{
                            background: "#111124",
                            height: "100%",
                            maxHeight: "calc(100% - 32px)",
                          }}
                        />
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none z-20"
                          style={{ background: "rgba(21, 21, 43, 0.15)" }}
                        >
                          <div className="flex gap-3 pointer-events-auto">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(url);
                              }}
                              className="bg-black/80 hover:bg-pink-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Download"
                            >
                              <Download size={20} />
                            </button>
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="bg-black/80 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Open in new tab"
                            >
                              <ExternalLink size={20} />
                            </a>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(a.id);
                              }}
                              className="bg-black/80 hover:bg-red-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Delete"
                            >
                              <Trash2 size={20} />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Infinite loader */}
            {hasMore && !initialLoading && (
              <div
                ref={loaderRef}
                className="w-full flex justify-center py-8 pointer-events-none"
              >
                {loadingMore && (
                  <img
                    src="/spinner.gif"
                    alt="Loading..."
                    className="w-7 h-7 mx-auto"
                  />
                )}
              </div>
            )}
          </>
        )}

        {/* Create Modal */}
        {modalOpen && (
          <GeneratePhotoshootAnyshootModal
            projectAssets={projectAssets}
            projectId={projectId}
            onClose={() => setModalOpen(false)}
            // The modal should call onGenerate with the same body it used before
            onGenerate={(body) => handleGenerate(body)}
          />
        )}

        {/* Preview Modal */}
        {preview && (
          <div
            ref={previewBackdrop}
            className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center px-2 select-none"
            style={{ animation: "fadeIn 0.18s" }}
          >
            <div className="relative bg-[#22223a] rounded-2xl max-w-[98vw] shadow-2xl p-0 flex flex-col">
              <button
                onClick={() => setPreview(null)}
                className="absolute top-2 right-2 bg-white/90 hover:bg-pink-600 text-black hover:text-white rounded-full p-2 shadow transition"
                title="Close"
              >
                <X size={21} />
              </button>
              <div className="p-6 flex flex-col items-center justify-center">
                <img
                  src={preview.url}
                  alt="Preview"
                  className="max-w-[95vw] max-h-[78vh] object-contain rounded-xl bg-black"
                  style={{ boxShadow: "0 4px 32px 0 rgba(30,25,60,0.5)" }}
                  draggable={false}
                />
              </div>
              <div className="flex justify-center gap-4 pb-5">
                <button
                  onClick={() => handleDownload(preview.url)}
                  className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Download
                </button>
                <button
                  onClick={() => handleDelete(preview.id)}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Delete
                </button>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Open in new tab
                </a>
              </div>
              {error && (
                <div className="text-red-500 mt-2 px-6 pb-3">{error}</div>
              )}
            </div>
          </div>
        )}

        {error && <div className="text-red-500 mt-4">{error}</div>}
      </div>
    </div>
  );
}
